import {
  ExpressionPostfixOnColumnName,
  ILineMetaNewExpression,
  ILineMetaType,
  IParsedChartDataExpression,
  IParsedChartDataRecord,
  IParsedExpressionColumn,
} from '../../components/Chart/interfaces';
import { MaybeNil } from '../interfaces';
import { isNil } from 'lodash';
import {
  IExpressionColumnFieldsFragment,
  IExpressionSliceFieldsFragment,
  IMeasureControl,
  QueryType,
} from '../apollo/graphqlSchema';
import { assertNotNil } from '../types';
import {
  generateHighlights,
  parseAliasReplacements,
  toComparisonAliasableSlice,
  toDefaultHasAlias,
} from './util';
import { getCharacterByIncrementedIndex } from '../character';
import {
  IComparisonMergeExpressionColumn,
  mergeComparisonData,
} from './mergeExpressionComparisonData';
import { COMPARISON_KEY_SUFFIX } from '../../config/constants';
import { IExpressionAnalysis } from '../apollo/responseTypes';

/* ******************** New data transforming methods for Expression *************************** */
/**
 * @param slices raw slices from the server
 * @param base base line as slice from server, if exists, combine it into slices
 * @returns combined slices with base and slice from server
 */
const parseSlicesHandler = (
  slices: IExpressionSliceFieldsFragment[],
  base: MaybeNil<IExpressionSliceFieldsFragment>,
) => {
  const parsedSlices: IExpressionSliceFieldsFragment[] = [...slices];
  // if base and slices both are exist, then we need to add base to slices.
  if (base) {
    // add base as the first slice
    parsedSlices.unshift(base);
  }
  return parsedSlices;
};

const DEFAULT_NAME = '';

// insert measure key, slice key into columns, so we can find column with key in the chart
const parseMeasureColumnsHandler = (
  columns: IExpressionColumnFieldsFragment[],
  slices: IExpressionSliceFieldsFragment[],
): IParsedExpressionColumn[] => {
  const parsedColumns: IParsedExpressionColumn[] = [];
  for (const slice of slices) {
    // iterate variables and measure to get all columns, then insert slice key and measure key.
    for (const variableIndex of slice.variables) {
      const column = columns[variableIndex];
      // XXX technically we could use (column as any).isComparison? ?? false
      // because comparison columns will have th extra prop via IComparisonMergeExpressionColumn
      const isComparison = isMappedComparisonKey(column.key);
      // XXX any column which is comparison is actually IComparisonMergeExpressionColumn
      const isFutureComparison =
        isComparison &&
        (column as IComparisonMergeExpressionColumn).isFutureComparison;
      parsedColumns.push({
        ...column,
        sliceKey: slice.key,
        sliceName: DEFAULT_NAME,
        measureName: DEFAULT_NAME,
        isComparison,
        isFutureComparison,
      });
    }

    // If exists, add result column
    const resultColumn = getExpressionResultMeasureColumn(slice, columns);

    if (resultColumn) {
      parsedColumns.push({
        ...resultColumn,
        sliceKey: slice.key,
        measureName: DEFAULT_NAME,
        sliceName: DEFAULT_NAME,
        isComparison: isMappedComparisonKey(resultColumn.key),
      });
    }
  }

  return parsedColumns;
};

const generateChartDataFromColumns = (
  parsedColumns: IParsedExpressionColumn[],
  dates: string[],
  live: boolean[],
): [IParsedChartDataRecord[], Record<string, string[]>] => {
  const chartData: IParsedChartDataRecord[] = [];
  let measureToDataKeyObject: Record<string, string[]> = {};
  for (let i = 0; i < dates.length; i++) {
    const date = dates[i];
    const dataForDay: IParsedChartDataRecord = { date };
    for (const column of parsedColumns) {
      const {
        key,
        basePercents,
        percentsPreviousValue,
        cumulativeSum,
        values,
        reading,
        measureKey,
      } = column;
      dataForDay[key] = values[i];
      if (!measureToDataKeyObject[measureKey]) {
        measureToDataKeyObject[measureKey] = [];
      }
      measureToDataKeyObject[measureKey].push(key);
      // if current column is not result measure, add percent value in data.
      if (basePercents.length > 0) {
        dataForDay[key + ExpressionPostfixOnColumnName.percentKey] =
          basePercents[i];
        measureToDataKeyObject[measureKey].push(
          key + ExpressionPostfixOnColumnName.percentKey,
        );
      }
      if (percentsPreviousValue.length > 0) {
        dataForDay[
          key + ExpressionPostfixOnColumnName.percentsPreviousValueKey
        ] = percentsPreviousValue[i];
        measureToDataKeyObject[measureKey].push(
          key + ExpressionPostfixOnColumnName.percentsPreviousValueKey,
        );
      }

      if (cumulativeSum) {
        dataForDay[key + ExpressionPostfixOnColumnName.cumulativeKey] =
          cumulativeSum.values[i];
        measureToDataKeyObject[measureKey].push(
          key + ExpressionPostfixOnColumnName.cumulativeKey,
        );
      }

      if (reading) {
        dataForDay[key + ExpressionPostfixOnColumnName.maKey] =
          reading.ma?.values[i] ?? null;
        dataForDay[key + ExpressionPostfixOnColumnName.ma30Key] =
          reading.ma30?.values[i] ?? null;
        dataForDay[key + ExpressionPostfixOnColumnName.ms7Key] =
          reading.ms7?.values[i] ?? null;
        dataForDay[key + ExpressionPostfixOnColumnName.ms30Key] =
          reading.ms30?.values[i] ?? null;

        measureToDataKeyObject[measureKey].push(
          key + ExpressionPostfixOnColumnName.maKey,
        );
        measureToDataKeyObject[measureKey].push(
          key + ExpressionPostfixOnColumnName.ma30Key,
        );
        measureToDataKeyObject[measureKey].push(
          key + ExpressionPostfixOnColumnName.ms7Key,
        );
        measureToDataKeyObject[measureKey].push(
          key + ExpressionPostfixOnColumnName.ms30Key,
        );
      }
    }
    dataForDay.isLive = live[i];
    chartData.push(dataForDay);
  }
  return [chartData, measureToDataKeyObject];
};

// use column key as index, generate all meta data for column.
// e.g. cumulative line name and key, percentage line name and key, ma line name and key.
export const generateLinesMetaFromColumns = (
  parsedColumns: IParsedExpressionColumn[],
  measureControls: IMeasureControl[],
  ignoreSliceNameOnDisplay: boolean,
  resultMeasureDisplay?: string,
): ILineMetaNewExpression => {
  const linesMeta: ILineMetaNewExpression = {};
  for (const column of parsedColumns) {
    const { key, name, measureKey, measureName, sliceName } = column;
    let measurePrefix: string | undefined = resultMeasureDisplay ?? 'A';
    if (measureControls.length > 1) {
      // if not found or this is result measure, there is no measurePrefix.
      const measureControl = measureControls.find(
        (control) => control.key === measureKey,
      );
      if (measureControl?.isResultKey) {
        measurePrefix = undefined;
      } else {
        const measureIndex = measureControls
          .filter((mc) => !mc.isResultKey)
          .findIndex((mc) => mc.key === measureKey);
        measurePrefix =
          measureIndex >= 0
            ? getCharacterByIncrementedIndex('A', measureIndex)
            : undefined;
      }
    }

    let nameWithPrefix = name;
    // if there is no breakdown, we don't need to display slice name.
    // Special case: Overall exists on data.Slices for every expression
    // The output should be : {SliceName} - A:{MeasureName} - {postFixName}.
    // the sliceName and postFixName is optional
    if (measurePrefix) {
      if (ignoreSliceNameOnDisplay) {
        nameWithPrefix = `${measurePrefix}: ` + name;
      } else {
        nameWithPrefix = `${sliceName} - ${measurePrefix}: ${measureName}`;
      }
    }

    linesMeta[key] = {
      name: key,
      display: nameWithPrefix,
      measureKey,
      measureName,
      measureLabel: measurePrefix,
      cumulativeLineName: name + ExpressionPostfixOnColumnName.cumulativeName,
      cumulativeLineKey: key + ExpressionPostfixOnColumnName.cumulativeKey,
      percentLineName: name,
      percentLineKey: key + ExpressionPostfixOnColumnName.percentKey,
      maLineName: name + ExpressionPostfixOnColumnName.maName,
      maLineKey: key + ExpressionPostfixOnColumnName.maKey,
      ma30LineName: name + ExpressionPostfixOnColumnName.ma30Name,
      ma30LineKey: key + ExpressionPostfixOnColumnName.ma30Key,
      ms7LineName: name + ExpressionPostfixOnColumnName.ms7Name,
      ms7LineKey: key + ExpressionPostfixOnColumnName.ms7Key,
      ms30LineName: name + ExpressionPostfixOnColumnName.ms30Name,
      ms30LineKey: key + ExpressionPostfixOnColumnName.ms30Key,
      percentChangeLineKey:
        key + ExpressionPostfixOnColumnName.percentsPreviousValueKey,
      sliceKey: column.sliceKey,
      sliceName: column.sliceName,
      type: ILineMetaType.Base,
    };

    linesMeta[key + ExpressionPostfixOnColumnName.cumulativeKey] = {
      name: key + ExpressionPostfixOnColumnName.cumulativeKey,
      display: nameWithPrefix + ExpressionPostfixOnColumnName.cumulativeName,
      measureKey,
      measureName,
      measureLabel: measurePrefix,
      parentKey: key,
      sliceKey: column.sliceKey,
      sliceName: column.sliceName,
      type: ILineMetaType.Cumulative,
    };

    linesMeta[key + ExpressionPostfixOnColumnName.percentKey] = {
      name: key + ExpressionPostfixOnColumnName.percentKey,
      display: nameWithPrefix,
      measureKey,
      measureName,
      measureLabel: measurePrefix,
      parentKey: key,
      sliceKey: column.sliceKey,
      sliceName: column.sliceName,
      percentChangeLineKey:
        key + ExpressionPostfixOnColumnName.percentsPreviousValueKey,
      type: ILineMetaType.Percent,
    };
    linesMeta[key + ExpressionPostfixOnColumnName.maKey] = {
      name: key + ExpressionPostfixOnColumnName.maKey,
      display: nameWithPrefix + ExpressionPostfixOnColumnName.maName,
      measureKey,
      measureName,
      measureLabel: measurePrefix,
      parentKey: key,
      sliceKey: column.sliceKey,
      sliceName: column.sliceName,
      type: ILineMetaType.MovingAverage,
    };
    linesMeta[key + ExpressionPostfixOnColumnName.ma30Key] = {
      name: key + ExpressionPostfixOnColumnName.ma30Key,
      display: nameWithPrefix + ExpressionPostfixOnColumnName.ma30Name,
      measureKey,
      measureName,
      measureLabel: measurePrefix,
      parentKey: key,
      sliceKey: column.sliceKey,
      sliceName: column.sliceName,
      type: ILineMetaType.MovingAverage30,
    };
    linesMeta[key + ExpressionPostfixOnColumnName.ms7Key] = {
      name: key + ExpressionPostfixOnColumnName.ms7Key,
      display: nameWithPrefix + ExpressionPostfixOnColumnName.ms7Name,
      measureKey,
      measureName,
      measureLabel: measurePrefix,
      parentKey: key,
      sliceKey: column.sliceKey,
      sliceName: column.sliceName,
      type: ILineMetaType.MovingSum7,
    };
    linesMeta[key + ExpressionPostfixOnColumnName.ms30Key] = {
      name: key + ExpressionPostfixOnColumnName.ms30Key,
      display: nameWithPrefix + ExpressionPostfixOnColumnName.ms30Name,
      measureKey,
      measureName,
      measureLabel: measurePrefix,
      parentKey: key,
      sliceKey: column.sliceKey,
      sliceName: column.sliceName,
      type: ILineMetaType.MovingSum30,
    };
  }
  return linesMeta;
};

// XXX - a bit of a hack here - this assumes that Kobol will not send
// a slice with a key ending with -comparison. The alternative is to keep track
// of a flag as part of the slice, but in that case may as well just do this
// entire merging on the Kobol side. Note that we could also use
// ((slice as any).isComparison? ?? false) because any
// comparison slice here would actually be IMergedExpressionSlice
export const isMappedComparisonKey = (key: string) =>
  key.endsWith(COMPARISON_KEY_SUFFIX);

/**
 * ExpressionSlice can have optional resultMeasureColumnIndex field.
 * If exists, resultMeasureColumnIndex will refer to a column with result measure
 */
export const getExpressionResultMeasureColumn = (
  expressionSlice: IExpressionSliceFieldsFragment,
  expressionColumns: IExpressionColumnFieldsFragment[],
): IExpressionColumnFieldsFragment | null => {
  if (!isNil(expressionSlice?.resultMeasureColumnIndex)) {
    const resultColumn =
      expressionColumns[expressionSlice.resultMeasureColumnIndex];

    return resultColumn || null;
  }

  return null;
};

export default function transformExpressionData(
  analysis: IExpressionAnalysis,
): IParsedChartDataExpression {
  assertNotNil(analysis.data);
  const comparisonData = analysis.comparisonData;

  // Compare mode special: If exists, merge analysis data with comparisonData
  if (!isNil(comparisonData) && !comparisonData.isEmpty) {
    analysis.data = mergeComparisonData(analysis.data, comparisonData);
  }

  assertNotNil(analysis.data);
  const {
    base,
    columns,
    dates,
    expressionAxis: xAxis,
    slices,
    statistics,
    live,
  } = analysis.data;
  const measureControls =
    analysis.chartControl.measureControls.map(toDefaultHasAlias);
  // combine base and slices together
  const parsedSlices = parseSlicesHandler(slices, base).map((s) => {
    return { ...toComparisonAliasableSlice(s, isMappedComparisonKey(s.key)) };
  });
  // generate measures based on measure names
  const parsedColumns = parseMeasureColumnsHandler(columns, parsedSlices);
  const linesMeta = generateLinesMetaFromColumns(
    parsedColumns,
    measureControls,
    analysis.groupbys.length === 0 && analysis.comparisonData == null,
    analysis?.query?.name ?? undefined,
  );
  const [data, dataKeysOnMeasure] = generateChartDataFromColumns(
    parsedColumns,
    dates,
    live,
  );
  const highlights = generateHighlights(parsedColumns);

  const aliasReplacements = parseAliasReplacements(
    analysis.pref.aliasReplacements ?? [],
  );

  // Kobol returns positive numbers on MA and CUMULATIVE numbers,
  // therefore only need to collect values from ExpressionColumn
  const negativeValueDictionary = parsedColumns.reduce<Record<string, boolean>>(
    (acc, column) => {
      acc[column.key] = column.values.some(
        (value) => value !== null && value < 0,
      );
      return acc;
    },
    {},
  );

  return {
    queryType: QueryType.Expression,
    dates,
    data,
    linesMeta,
    statistics,
    slices: parsedSlices,
    columns: parsedColumns,
    measureControls,
    aliasReplacements,
    aliasReplacementsSnapshot: aliasReplacements,
    highlights,
    dataKeysOnMeasure,
    xAxis,
    negativeValueDictionary,
  };
}
